<template>
    <DModal
        :show="showModal"
        :is-awaiting-validation="true"
        :title="__('tools.testing.snapshots.apply.modal.title')"
        :message="__('tools.testing.snapshots.apply.modal.message', {'snapshot': config.display_name ?? item})"
        is-confirm
        is-error
        is-medium
        @close="close"
        @confirm="confirm"
    >
        <div class="modal-content px-4 mb-4">
            <DAlert
                v-if="errors && errors.general_error"
                color="danger"
                class="mt-2"
            >
                {{ errors.general_error }}
            </DAlert>
            <CheckReport
                :results="checkResults"
                :safe-message="__('tools.testing.snapshots.apply.modal.form.hint_check_no_violations')"
                :unsafe-message="__('tools.testing.snapshots.apply.modal.form.hint_check_has_violations')"
                :footer="checkResults.length > 0 ? __('tools.testing.snapshots.apply.modal.form.confirm_check_has_violations') : null"
                class="mb-3"
            />

            <div class="border rounded p-3 mb-3">
                <DFormRow
                    v-if="sections.settings === true"
                    :label="__('tools.testing.snapshots.apply.modal.form.settings')"
                    is-required
                    :error="errors?.settings"
                    :hint="`${__('tools.testing.snapshots.apply.modal.form.settings_hint')}<br>${config.include_secrets === true ? __('tools.testing.snapshots.apply.modal.form.hint_secrets') : __('tools.testing.snapshots.apply.modal.form.hint_no_secrets')}`"
                >
                    <DSwitch
                        id="settings"
                        v-model="form.settings"
                        color="success"
                    />
                </DFormRow>
                <DFormRow
                    v-if="sections.products === true"
                    :label="__('tools.testing.snapshots.apply.modal.form.products')"
                    is-required
                    :error="errors?.products"
                    :hint="__('tools.testing.snapshots.apply.modal.form.products_hint')"
                >
                    <DSwitch
                        id="products"
                        v-model="form.products"
                        color="success"
                    />
                </DFormRow>
                <DFormRow
                    v-if="sections.email_templates === true"
                    :label="__('tools.testing.snapshots.apply.modal.form.email_templates')"
                    is-required
                    :error="errors?.email_templates"
                    :hint="__('tools.testing.snapshots.apply.modal.form.email_templates_hint')"
                >
                    <DSwitch
                        id="email_templates"
                        v-model="form.emailTemplates"
                        color="success"
                    />
                </DFormRow>
                <DFormRow
                    v-if="sections.sms_templates === true"
                    :label="__('tools.testing.snapshots.apply.modal.form.sms_templates')"
                    is-required
                    :error="errors?.sms_templates"
                    :hint="__('tools.testing.snapshots.apply.modal.form.sms_templates_hint')"
                >
                    <DSwitch
                        id="sms_templates"
                        v-model="form.smsTemplates"
                        color="success"
                    />
                </DFormRow>
                <DFormRow
                    v-if="sections.customers === true"
                    :label="__('tools.testing.snapshots.apply.modal.form.customers')"
                    is-required
                    :error="errors?.customers"
                    :hint="__('tools.testing.snapshots.apply.modal.form.customers_hint')"
                >
                    <DSwitch
                        id="customers"
                        v-model="form.customers"
                        color="success"
                    />
                </DFormRow>
                <DFormRow
                    v-if="sections.settings === true"
                    :label="__('tools.testing.snapshots.apply.modal.form.preserve_name')"
                    is-required
                    :error="errors?.preserveTenantName"
                    :hint="__('tools.testing.snapshots.apply.modal.form.preserve_name_hint')"
                >
                    <DSwitch
                        id="preserve_name"
                        v-model="form.preserveTenantName"
                        color="success"
                    />
                </DFormRow>
            </div>
            <div class="border rounded p-3 mb-3">
                <label class="form-label fw-semibold">{{ __('tools.testing.snapshots.apply.modal.form.apply_mode') }}</label>
                <div>
                    <DFormCheck
                        id="apply_mode_replace"
                        type="radio"
                        name="applyMode"
                        :label="__('tools.testing.snapshots.apply.modal.form.apply_mode_replace')"
                        :checked="form.applyMode === 'replace'"
                        @change="form.applyMode = 'replace'"
                    />
                    <p class="text-muted small ms-4 mt-0 mb-2">
                        {{ __('tools.testing.snapshots.apply.modal.form.apply_mode_replace_hint') }}
                    </p>
                    <DFormCheck
                        id="apply_mode_insert"
                        type="radio"
                        name="applyMode"
                        :label="__('tools.testing.snapshots.apply.modal.form.apply_mode_insert')"
                        :checked="form.applyMode === 'insert'"
                        @change="form.applyMode = 'insert'"
                    />
                    <p class="text-muted small ms-4 mt-0 mb-0">
                        {{ __('tools.testing.snapshots.apply.modal.form.apply_mode_insert_hint') }}
                    </p>
                </div>
                <DAlert
                    v-if="form.applyMode === 'replace' && form.customers === true"
                    color="warning"
                    class="mt-3 mb-0 py-2"
                >
                    {{ __('tools.testing.snapshots.apply.modal.form.replace_customers_warning', { customers: limits.customers ?? '?', contracts: limits.contracts ?? '?' }) }}
                </DAlert>
            </div>
        </div>
        <template #actions>
            <DButton
                color="secondary"
                icon="eye-outline"
                :label="__('tools.testing.snapshots.apply.modal.form.generate_preview')"
                @click="requestPreview"
            />
        </template>
    </DModal>
</template>

<script setup>

import { __ } from '~@/Composables/translation'
import { ref } from 'vue'
import { useForm } from '@inertiajs/vue3'
import CheckReport from '~@/Components/Tools/Testing/Snapshots/CheckReport.vue'

const props = defineProps({
    showModal: {
        type: Boolean,
        default: false
    },
    item: {
        type: String,
        default: ''
    },
    sections: {
        type: Object,
        default: () => { return {} }
    },
    config: {
        type: Object,
        default: () => { return {} }
    },
    errors: {
        type: Object,
        default: () => { return {} }
    },
    checkResults: {
        type: Array,
        default: () => { return [] }
    },
    limits: {
        type: Object,
        default: () => { return {} }
    }
})

const itemRef = ref(props.item)
const emit = defineEmits(['close', 'confirm', 'preview'])

const form = useForm({
    settings: props.sections.settings ?? false,
    products: props.sections.products ?? false,
    emailTemplates: props.sections.email_templates ?? false,
    smsTemplates: props.sections.sms_templates ?? false,
    customers: props.sections.customers ?? false,
    preserveTenantName: true,
    applyMode: 'replace',
})

function buildSectionData () {
    const data = {}

    if (props.sections.settings === true) {
        data.settings = form.settings ?? false
        data.preserveTenantName = form.preserveTenantName ?? false
    }
    if (props.sections.products === true) {
        data.products = form.products ?? false
    }
    if (props.sections.email_templates === true) {
        data.emailTemplates = form.emailTemplates ?? false
    }
    if (props.sections.sms_templates === true) {
        data.smsTemplates = form.smsTemplates ?? false
    }
    if (props.sections.customers === true) {
        data.customers = form.customers ?? false
    }

    return data
}

function confirm () {
    const data = buildSectionData()
    data.applyMode = form.applyMode
    emit('confirm', itemRef.value, data)
}

function requestPreview () {
    const data = buildSectionData()
    data.applyMode = form.applyMode
    emit('preview', itemRef.value, data)
}

function close () {
    emit('close')
    form.reset()
}
</script>

<style lang="scss" scoped>
.modal-content {
    box-shadow: none;
}

.modal-content h2, .modal-content p {
    color: var(--cui-body-color);
    opacity: 0.6;
}
</style>
